import { useMemo } from 'react';

import { fetchAllMatches, fetchAllTeams } from 'api/ScheduleAPI';
import { ActionCls, CrimsonCls, Emojis } from 'constants/constants';
import { SqRoutes } from 'constants/Routes';
import { useAppDispatch, useAppSelector } from 'hooks/hooks';
import { SubmitOptions, SubmitTarget, useSubmit } from 'react-router-dom';
import { getEnhancedMatches, setMatches, setTeams } from 'slices/SqScheduleSlice';
import { getAvailableToJoinGames, getCompletedGames, getResumeGames } from 'slices/SquaresSlice';
import { getFbSqUser, getUserAuthState } from 'slices/SqUserSlice';
import { Maybe } from 'types/App.types';
import { ScheduledMatch, TeamModel } from 'types/Schedule.types';
import { FbSqUser, SqGameID, SqGamePayloadAction, SqGamePayloadActionType } from 'types/SqGame.types';

export type UsePlayerManagerHook = ReturnType<typeof useLobbyManager>;

const useLobbyManager = () => {
  const submit = useSubmit();
  const doSubmit = (options: SubmitOptions, target: SubmitTarget = {}) => submit(target, options);
  const dispatch = useAppDispatch();

  const user: Maybe<FbSqUser> = useAppSelector(getFbSqUser);
  const resumeGamesList = useAppSelector(getResumeGames);
  const joinGamesList = useAppSelector(getAvailableToJoinGames);
  const completedGamesList = useAppSelector(getCompletedGames);
  const authState = useAppSelector(getUserAuthState);
  const matches = useAppSelector(getEnhancedMatches);

  const counts = useMemo(() => {
    if (user && resumeGamesList && joinGamesList && completedGamesList) {
      return {
        resume: resumeGamesList.length,
        join: joinGamesList.length,
        completed: completedGamesList.length
      };
    }
    return { resume: 0, join: 0, completed: 0 };
  }, [user, resumeGamesList, joinGamesList, completedGamesList]);

  const loadTeamsAndMatches = () => {
    const teamsCb = (teams: TeamModel[]) => dispatch(setTeams(teams));
    const matchesCb = (matches: ScheduledMatch[]) => dispatch(setMatches(matches));
    fetchAllTeams(teamsCb);
    fetchAllMatches(matchesCb);
  };

  const getPayloadData = (
    type: SqGamePayloadActionType,
    gameId: SqGameID = '',
    gameName: string = '',
    matchId: string = ''
  ): SqGamePayloadAction => {
    return {
      type: type,
      gameId,
      gameName,
      matchId,
      redirectUrl: gameId.length > 0 ? SqRoutes.getGameRoute(gameId) : SqRoutes.Game,
      playerId: user!.uid,
      playerName: user!.name,
    };
  };

  const submitDataToAction = (method: 'PUT' | 'POST', payload: SqGamePayloadAction) => {
    doSubmit({ method, action: SqRoutes.Lobby, viewTransition: true }, { payload: JSON.stringify(payload) });
  };

  const onCreateGame = (title: string, matchId: string) => {
    if (title.length <= 0 || !matchId) {
      const noTitle = `%c ${'EMPTY/MISSING TITLE'} %c`;
      const noMatchSelected = `%c ${'MATCH NOT SELECTED'} %c`;
      const bothErrors = '%c EMPTY/MISSING TITLE & MATCH NOT SELECTED %c';

      const msg = title.length <= 0 && !matchId ? bothErrors : !matchId ? noMatchSelected : noTitle;
      const errorMsg = `${Emojis.warning} %c CANNOT %c %c CREATE GAME %c ${msg}`;
      console.log(errorMsg, CrimsonCls, '', ActionCls, '', CrimsonCls, '');
      return;
    }

    const payloadData: SqGamePayloadAction = getPayloadData(SqGamePayloadActionType.CreateGame, '', title, matchId);
    submitDataToAction('PUT', { ...payloadData, gameName: title, matchId });
  };

  const onResumeGame = (gameId: SqGameID, title: string) => {
    const payloadData: SqGamePayloadAction = getPayloadData(SqGamePayloadActionType.ResumeGame, gameId, title);
    submitDataToAction('POST', payloadData);
  };

  const onJoinGame = (gameId: SqGameID, title: string) => {
    const payloadData: SqGamePayloadAction = getPayloadData(SqGamePayloadActionType.JoinGame, gameId, title);
    submitDataToAction('POST', payloadData);
  };

  return {
    onCreateGame,
    onJoinGame,
    onResumeGame,
    loadTeamsAndMatches,
    user,
    playerName: user?.name ?? '',
    playerId: user?.uid ?? '',
    resumeGamesList,
    joinGamesList,
    completedGamesList,
    matches,
    counts,
    authState,
  };
};

export default useLobbyManager;
